/**
 * MediaLightbox.tsx — full-screen media viewer (Phase 6; source-agnostic since Phase 6 UI).
 *
 * Opened by clicking a thumbnail in MessageThread (chat attachments) OR the expand
 * control in VaultPickerModal (vault content, so a chatter can check it before sending).
 * Renders the full-size media:
 *   - picture → the full image, streamed through our EU media-proxy (`/full`); the
 *     browser never touches Google GCS.
 *   - video   → the HLS stream proxied through our API (`/hls`), played with hls.js
 *     (native HLS on Safari). Owner decision 2026-06-29: the Bunny iframe embed is
 *     referrer-locked to maloum.com (403 on our origin), so we proxy the stream;
 *     the browser never touches Bunny → the Art.9 media path stays EU-clean.
 *
 * The viewer is decoupled from any particular API route: the caller passes a `srcFor`
 * builder that maps (uploadId, variant) → a same-origin proxy URL. Chat builds the
 * conversation-scoped path; the vault picker builds the vault-scoped path. Both stream
 * through the same EU media-proxy primitives server-side.
 *
 * The operator is the creator, so locked PPV content is shown in full here — the
 * lightbox only annotates the sale status ("Fan hasn't unlocked"), it never hides it.
 *
 * A11y: role=dialog/aria-modal, ESC + backdrop close, arrow-key navigation across the
 * item set, focus moved into the dialog on open and restored on close.
 */

import * as React from 'react';
import { createPortal } from 'react-dom';
import Hls from 'hls.js';

export interface LightboxPpvInfo {
  /** formatted price, e.g. "€5.00" — null when unknown */
  priceLabel: string | null;
  /** true when the fan has not purchased yet (operator still sees the content) */
  locked: boolean;
}

/**
 * Minimal media descriptor the viewer needs. Structurally satisfied by the chat
 * `MessageMediaDto` and the vault `VaultMediaItemDto` alike — the caller passes
 * whichever it has.
 */
export interface LightboxItem {
  uploadId: string;
  /** 'video' | 'picture' | … — drives image-vs-HLS rendering. */
  type: string;
  width?: number | null;
  height?: number | null;
  /**
   * true when a full-size source exists at rest. When false, a picture falls back to
   * its thumbnail variant. Defaults to true when omitted.
   */
  hasFull?: boolean;
}

/** Build a same-origin EU-media-proxy URL for one media item + variant. */
export type LightboxSrcFor = (uploadId: string, variant: 'full' | 'thumb' | 'hls') => string;

interface MediaLightboxProps {
  items: LightboxItem[];
  startIndex: number;
  /** Maps (uploadId, variant) → a same-origin proxy URL (chat- or vault-scoped). */
  srcFor: LightboxSrcFor;
  ppv?: LightboxPpvInfo;
  onClose: () => void;
}

export function MediaLightbox({
  items,
  startIndex,
  srcFor,
  ppv,
  onClose,
}: MediaLightboxProps) {
  const [index, setIndex] = React.useState(startIndex);
  const closeBtnRef = React.useRef<HTMLButtonElement>(null);
  const restoreFocusRef = React.useRef<HTMLElement | null>(null);

  const count = items.length;
  const current = items[index];
  const isVideo = current?.type === 'video';

  const go = React.useCallback(
    (delta: number) => setIndex((i) => (i + delta + count) % count),
    [count],
  );

  // Keyboard: ESC closes, ←/→ navigate (when multiple).
  React.useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      } else if (count > 1 && e.key === 'ArrowRight') {
        e.preventDefault();
        go(1);
      } else if (count > 1 && e.key === 'ArrowLeft') {
        e.preventDefault();
        go(-1);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose, go, count]);

  // Move focus into the dialog on open; restore it on close. Lock body scroll.
  React.useEffect(() => {
    restoreFocusRef.current = document.activeElement as HTMLElement | null;
    closeBtnRef.current?.focus();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
      restoreFocusRef.current?.focus?.();
    };
  }, []);

  if (!current) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex flex-col bg-black/90"
      role="dialog"
      aria-modal="true"
      aria-label="Media viewer"
    >
      {/* Backdrop — click anywhere outside the media to close */}
      <div className="absolute inset-0" onClick={onClose} aria-hidden="true" />

      {/* Top bar: counter + close */}
      <div className="relative z-10 flex items-center justify-between px-4 py-3 text-white/90">
        <span className="text-xs font-mono">
          {count > 1 ? `${index + 1} / ${count}` : ''}
        </span>
        <button
          ref={closeBtnRef}
          onClick={onClose}
          aria-label="Close media viewer"
          className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-lg text-white hover:bg-white/20 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black"
        >
          ×
        </button>
      </div>

      {/* Stage */}
      <div className="relative z-10 flex flex-1 items-center justify-center px-4 pb-2 min-h-0">
        {/* Prev */}
        {count > 1 && (
          <button
            onClick={() => go(-1)}
            aria-label="Previous media"
            className="absolute left-2 top-1/2 -translate-y-1/2 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-2xl text-white hover:bg-white/20 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
          >
            ‹
          </button>
        )}

        <MediaStage
          key={current.uploadId}
          srcFor={srcFor}
          item={current}
          isVideo={isVideo}
        />

        {/* Next */}
        {count > 1 && (
          <button
            onClick={() => go(1)}
            aria-label="Next media"
            className="absolute right-2 top-1/2 -translate-y-1/2 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-2xl text-white hover:bg-white/20 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
          >
            ›
          </button>
        )}
      </div>

      {/* Caption: PPV sale status (operator-only context) */}
      {ppv && (
        <div className="relative z-10 flex items-center justify-center gap-2 px-4 py-3 text-xs text-white/80">
          <span className="rounded bg-white/15 px-1.5 py-0.5 font-semibold tracking-wide">PPV</span>
          {ppv.priceLabel && <span className="font-mono">{ppv.priceLabel}</span>}
          <span aria-hidden="true">·</span>
          <span>{ppv.locked ? '🔒 Fan hasn’t unlocked yet' : 'Unlocked by fan'}</span>
        </div>
      )}
    </div>,
    document.body,
  );
}

// ---------------------------------------------------------------------------

function MediaStage({
  srcFor,
  item,
  isVideo,
}: {
  srcFor: LightboxSrcFor;
  item: LightboxItem;
  isVideo: boolean;
}) {
  // Aspect ratio from the stored dimensions; default to portrait (chat/vault media is
  // usually 9:16). Constrain to the viewport so portrait videos never overflow.
  const ar =
    item.width && item.height ? `${item.width} / ${item.height}` : isVideo ? '9 / 16' : undefined;

  if (isVideo) {
    return (
      <div
        className="relative max-h-full max-w-full"
        style={{ aspectRatio: ar, height: 'min(80vh, calc(90vw * 16 / 9))', maxWidth: '90vw' }}
      >
        <VideoPlayer srcFor={srcFor} uploadId={item.uploadId} />
      </div>
    );
  }

  return <FullImage srcFor={srcFor} uploadId={item.uploadId} hasFull={item.hasFull ?? true} />;
}

function FullImage({
  srcFor,
  uploadId,
  hasFull,
}: {
  srcFor: LightboxSrcFor;
  uploadId: string;
  hasFull: boolean;
}) {
  const [state, setState] = React.useState<'loading' | 'ready' | 'error'>(
    hasFull ? 'loading' : 'error',
  );
  // Re-fetch fresh on each open (signed URLs expire); fall back to thumb if no full.
  const src = srcFor(uploadId, hasFull ? 'full' : 'thumb');

  if (state === 'error') return <Unavailable kind="Image" />;

  return (
    <>
      {state === 'loading' && <Spinner />}
      <img
        src={src}
        alt="Full size media"
        onLoad={() => setState('ready')}
        onError={() => setState('error')}
        className={[
          'max-h-[82vh] max-w-[92vw] object-contain rounded-md',
          state === 'ready' ? '' : 'invisible absolute',
        ].join(' ')}
      />
    </>
  );
}

function VideoPlayer({
  srcFor,
  uploadId,
}: {
  srcFor: LightboxSrcFor;
  uploadId: string;
}) {
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const [error, setError] = React.useState(false);
  // Master playlist; variant playlists + .ts segments resolve back through the same
  // proxy route (the server rewrites child URIs to `hls?p=…`).
  const src = srcFor(uploadId, 'hls');

  React.useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Safari / iOS play HLS natively — no hls.js needed.
    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = src;
      // Stop fetching proxied segments on close/navigation.
      return () => {
        video.removeAttribute('src');
        video.load();
      };
    }
    if (Hls.isSupported()) {
      const hls = new Hls({
        xhrSetup: (xhr) => {
          xhr.withCredentials = true;
        },
      });
      hls.on(Hls.Events.ERROR, (_evt, data) => {
        if (data.fatal) setError(true);
      });
      hls.loadSource(src);
      hls.attachMedia(video);
      return () => hls.destroy();
    }
    setError(true);
  }, [src]);

  if (error) return <Unavailable kind="Video" />;
  return (
    <video
      ref={videoRef}
      controls
      autoPlay
      playsInline
      className="absolute inset-0 h-full w-full rounded-md bg-black"
    />
  );
}

function Spinner() {
  return (
    <div className="flex items-center justify-center text-white/70 text-sm" role="status">
      <span>Loading…</span>
    </div>
  );
}

function Unavailable({ kind }: { kind: 'Image' | 'Video' }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-md bg-white/5 px-8 py-10 text-center text-white/70">
      <span aria-hidden="true" className="text-2xl">
        📎
      </span>
      <span className="text-sm">{kind} unavailable</span>
      <span className="text-xs text-white/40">The signed link may have expired — re-ingest to refresh.</span>
    </div>
  );
}
